import React, { useMemo, useState } from 'react';
import { Dialog } from '@innovationnorway/cl-react';
import { useTranslation } from 'react-i18next';
import {
  ApiException,
  CreateSharedStatusDto,
  OneWayInOpportunityStatusCreateDto,
  OneWayInRecommendation,
  OneWayInServiceDto,
  OneWayInServiceProviderDto,
  OneWayInStatus,
  ServiceRecommendationDtoV2,
} from 'src/api/v2';
import { useParams } from 'react-router-dom';
import { PageParams } from '../../types/PageParams';
import useOneWayIn from 'src/features/one-way-in/hooks/use-one-way-in';
import { useUser } from 'src/features/authorization';
import { useCluster } from 'src/features/cluster';
import { HOTJAR_DECLARATIONS, SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import useHotjar from 'src/hooks/use-hotjar';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { toastPromise } from 'src/utils/toast';
import { sanitizeD365RichTextEditor } from 'src/features/one-way-in/utils/htmlTextConverter';
import getInitialActorReferMessageHtml from 'src/features/one-way-in/utils/getInitialActorReferMessageHtml';
import getInitialCustomerReferMessageHtml from 'src/features/one-way-in/utils/getInitialCustomerReferMessageHtml';
import createOwiUpdateDto from '../../utils/createOwiUpdateDto';
import useActorsWithAccess from '../../hooks/use-actors-with-access';
import { DialogState, DialogStates } from '../../constants/referModalDialogStates';
import SelectActorStep from './refer-modal-steps/SelectActorStep';
import SelectServicesStep from './refer-modal-steps/SelectServicesStep';
import WriteMessageToActorStep from './refer-modal-steps/WriteMessageToActorStep';
import AskCustomerMessageStep from './refer-modal-steps/AskCustomerMessageStep';
import WriteMessageToCustomerStep from './refer-modal-steps/WriteMessageToCustomerStep';
import ArchiveStep from './refer-modal-steps/ArchiveStep';
import { ActorFilters, emptyActorFilters } from './ChooseActor';

interface ReferModalProps {
  dialogOpen: boolean;
  onClose: () => void;
}

const ReferModal = ({ dialogOpen, onClose }: ReferModalProps) => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');

  const [dialogState, setDialogState] = useState<DialogState>(DialogStates.SelectActorToReferTo);

  const { formId } = useParams<PageParams>();
  const {
    shareOneWayInOpportunity,
    recommendService,
    updateOneWayInOpportunityStatus,
    oneWayInOpportunityById,
    oneWayInGroupedFormWOEnrollmentsQuery,
  } = useOneWayIn(formId);

  const { actors: actorsWithAccess, isLoading: actorsWithAccessLoading } = useActorsWithAccess(formId ?? '');

  const [actorSearchQuery, setActorSearchQuery] = useState('');
  const [actorFilters, setActorFilters] = useState<ActorFilters>(emptyActorFilters);
  const [selectedActorRows, setSelectedActorRows] = useState<OneWayInServiceProviderDto[]>([]);
  const [selectedServices, setSelectedServices] = useState<OneWayInServiceDto[]>([]);
  const referTo = selectedActorRows.length > 0 ? selectedActorRows[0] : undefined;
  const actorAlreadyHasOpportunity =
    referTo != undefined && (actorsWithAccess ?? []).some((actor) => actor.clusterId === referTo.id);

  const [actorMessage, setActorMessage] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');
  const [customerEmailSelection, setCustomerEmailSelection] = useState(false);

  const { isAadUser, user, displayName } = useUser();
  const { cluster } = useCluster();
  const siteimprove = useSiteimprove();
  const hotjar = useHotjar();

  const initialActorMessage = useMemo(
    () =>
      getInitialActorReferMessageHtml(
        cluster?.name,
        oneWayInGroupedFormWOEnrollmentsQuery.data?.tittel,
        displayName,
        selectedServices,
      ),
    [cluster?.name, oneWayInGroupedFormWOEnrollmentsQuery.data?.tittel, displayName, selectedServices],
  );

  const sanitizedActorMessage = useMemo(
    () => sanitizeD365RichTextEditor(actorMessage || initialActorMessage),
    [actorMessage, initialActorMessage],
  );

  const initialCustomerMessage = useMemo(
    () =>
      getInitialCustomerReferMessageHtml(
        referTo?.name,
        cluster?.name,
        oneWayInGroupedFormWOEnrollmentsQuery.data?.tittel,
        displayName,
      ),
    [referTo?.name, cluster?.name, oneWayInGroupedFormWOEnrollmentsQuery.data?.tittel, displayName],
  );

  const sanitizedCustomerMessage = useMemo(
    () => sanitizeD365RichTextEditor(customerMessage || initialCustomerMessage),
    [customerMessage, initialCustomerMessage],
  );

  const sendReferral = () => {
    const customerContactEmailAddress = oneWayInGroupedFormWOEnrollmentsQuery.data?.kontakt?.epost ?? '';
    const opportunityId = oneWayInOpportunityById.data?.id ?? '';
    const recipientEmail = oneWayInGroupedFormWOEnrollmentsQuery.data?.kontakt?.epost ?? '';
    const recipientName = oneWayInGroupedFormWOEnrollmentsQuery.data?.kontakt?.navn ?? '';

    const createDto: CreateSharedStatusDto = {
      receivingClusterIds: [referTo!.id],
      description: sanitizedActorMessage,
      sendToActor: true,
      sendToCustomer: customerEmailSelection,
      customerReceivingEmailAddresses: [customerContactEmailAddress],
      actorEmail: { message: sanitizedActorMessage },
      customerEmail: { message: sanitizedCustomerMessage },
      customerName: recipientName || '',
      handlersName: displayName,
    };

    if (selectedServices.length > 0) {
      const selectedServiceIds = selectedServices.map((service) => service.id);

      const opportunityStatusUpdate = {
        oneWayInOpportunityId: opportunityId,
        referenceClusterId: undefined,
        status: OneWayInStatus.Active,
        description: sanitizedActorMessage,
        createdOn: new Date(),
        closedReason: undefined,
        recommendedServices: selectedServiceIds,
        registeredServices: [],
        clusterContactId: undefined,
        clusterUserId: isAadUser ? undefined : user?.clusterUserId,
        handlersName: displayName || undefined,
        sendFollowUpReminderOn: undefined,
        admittedToProgram: false,
        sivaPhase: undefined,
        deliveredOn: undefined,
      } as OneWayInOpportunityStatusCreateDto;

      const serviceRecommendation = {
        recipientEmail: recipientEmail,
        recipientName: recipientName,
        services: selectedServices.map((service) => ({
          serviceName: service.name,
          links: service.externalLinks.map((link) => link.url),
          description: sanitizeD365RichTextEditor(service.description || ''),
        })),
        comment: '',
        senderName: displayName ?? '',
        senderOrganisation: cluster?.name || '',
        sendToActor: false,
        sendToCustomer: false,
      } as ServiceRecommendationDtoV2;

      const promise = recommendService.mutateAsync({
        opportunityStatusCreate: opportunityStatusUpdate,
        serviceRecommendation: serviceRecommendation,
        sharedStatusCreate: createDto,
      } as OneWayInRecommendation);

      toastPromise(promise, {
        pending: tOneWayIn('recommendToast.Pending'),
        success: tOneWayIn('recommendToast.Success'),
        error: tOneWayIn('recommendToast.Error'),
      }).catch(() => {
        siteimprove.log(
          SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.id,
          SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.REFERRAL_SEND_FAILURE,
          formId,
        );
      });
    } else {
      siteimprove.log(
        SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.id,
        SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.REFERRAL_SEND_ATTEMPT,
        formId,
      );
      const sharePromise = shareOneWayInOpportunity.mutateAsync({
        createDto,
        opportunityId: opportunityId || '',
      });

      toastPromise(sharePromise, {
        pending: tOneWayIn('referToast.Pending'),
        success: tOneWayIn('referToast.Success'),
        error: (ex) =>
          ex instanceof ApiException && ex.status === 409
            ? tOneWayIn('referToast.Error409')
            : tOneWayIn('referToast.Error'),
      })
        .then(() => {
          hotjar.log(
            `${HOTJAR_DECLARATIONS.UMUX.id} ${HOTJAR_DECLARATIONS.UMUX.actions.OPPORTUNITY_FORWARDED}`,
          );
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.id,
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.REFERRAL_SEND_SUCCESS,
            formId,
          );
        })
        .catch(() => {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.id,
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.REFERRAL_SEND_FAILURE,
            formId,
          );
        });
    }
  };

  const handleClose = () => {
    setDialogState(DialogStates.SelectActorToReferTo);
    setSelectedServices([]);
    setActorSearchQuery('');
    setActorFilters(emptyActorFilters);
    setSelectedActorRows([]);
    setActorMessage('');
    setCustomerMessage('');
    setCustomerEmailSelection(false);
    onClose();
  };

  const handleSend = () => {
    sendReferral();
    handleClose();
  };

  const handleSendWithoutCustomerMessage = () => {
    setCustomerMessage('');
    setCustomerEmailSelection(false);
    setDialogState(DialogStates.DoYouWantToArchivePrompt);
  };

  return (
    <>
      {(dialogState === DialogStates.SelectActorToReferTo ||
        dialogState === DialogStates.SelectServicesWithActor ||
        dialogState === DialogStates.WriteMessageToActor) && (
        <Dialog
          open={dialogOpen}
          onClose={handleClose}
          className="refer-dialog"
          aria-label={tOneWayIn('refer.dialog.ariaLabel')}
        >
          {dialogState === DialogStates.SelectActorToReferTo && (
            <SelectActorStep
              key={String(dialogOpen)}
              formId={formId}
              searchQuery={actorSearchQuery}
              setSearchQuery={setActorSearchQuery}
              selectedActorRows={selectedActorRows}
              setSelectedActorRows={setSelectedActorRows}
              filters={actorFilters}
              setFilters={setActorFilters}
              onNext={() => setDialogState(DialogStates.SelectServicesWithActor)}
            />
          )}
          {dialogState === DialogStates.SelectServicesWithActor && referTo != undefined && (
            <SelectServicesStep
              referTo={referTo}
              actorAlreadyHasOpportunity={actorAlreadyHasOpportunity}
              isLoading={actorsWithAccessLoading}
              selectedServices={selectedServices}
              setSelectedServices={setSelectedServices}
              onPrevious={() => setDialogState(DialogStates.SelectActorToReferTo)}
              onNext={() =>
                setDialogState(
                  actorAlreadyHasOpportunity
                    ? DialogStates.DoYouWantToArchivePrompt
                    : DialogStates.WriteMessageToActor,
                )
              }
            />
          )}
          {dialogState === DialogStates.WriteMessageToActor && referTo != undefined && (
            <WriteMessageToActorStep
              referTo={referTo}
              initialActorMessage={initialActorMessage}
              setActorMessage={setActorMessage}
              sanitizedActorMessage={sanitizedActorMessage}
              onPrevious={() => setDialogState(DialogStates.SelectServicesWithActor)}
              onNext={() => setDialogState(DialogStates.DoYouWantToWriteMessageToCustomerPrompt)}
            />
          )}
        </Dialog>
      )}

      {dialogState === DialogStates.DoYouWantToWriteMessageToCustomerPrompt && (
        <AskCustomerMessageStep
          dialogOpen={dialogOpen}
          onWriteMessage={() => {
            setCustomerEmailSelection(true);
            setDialogState(DialogStates.WriteMessageToCustomer);
          }}
          onSkip={() => setDialogState(DialogStates.DoYouWantToArchivePrompt)}
        />
      )}

      {dialogState === DialogStates.WriteMessageToCustomer && referTo != undefined && (
        <WriteMessageToCustomerStep
          dialogOpen={dialogOpen}
          onClose={handleSendWithoutCustomerMessage}
          initialCustomerMessage={initialCustomerMessage}
          setCustomerMessage={setCustomerMessage}
          sanitizedCustomerMessage={sanitizedCustomerMessage}
          onSendEmail={() => setDialogState(DialogStates.DoYouWantToArchivePrompt)}
        />
      )}

      {dialogState === DialogStates.DoYouWantToArchivePrompt && (
        <ArchiveStep
          dialogOpen={dialogOpen}
          onClose={handleClose}
          isLoading={updateOneWayInOpportunityStatus.isLoading}
          onArchiveAndSend={async () => {
            await updateOneWayInOpportunityStatus.mutateAsync(
              createOwiUpdateDto(
                oneWayInOpportunityById.data?.id,
                OneWayInStatus.Closed,
                '',
                isAadUser ? undefined : user?.clusterUserId,
                displayName || undefined,
              ),
            );
            sendReferral();
            handleClose();
          }}
          onSendWithoutArchive={handleSend}
        />
      )}
    </>
  );
};

export default ReferModal;
